import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Pencil } from "lucide-react";
import { useAgents } from "@/components/Agents/AgentsContext";
import { MarkdownArticleBody } from "@/components/MarkdownArticleBody";
import { apiGet } from "@/lib/apiClient";
import { isBookCollectionKey } from "@/lib/knowledgeBaseRegistry";
import {
  articleEditPath,
  formatKnowledgeDate,
  knowledgeStatusLabel,
  type KnowledgeArticle,
} from "@/lib/knowledgeModel";
import { agentsHref } from "@/lib/atwAgentsHost";
import {
  KnowledgeChrome,
  KnowledgeError,
  knowledgePrimaryLinkClass,
  knowledgeSecondaryLinkClass,
} from "@/pages/knowledge/KnowledgeChrome";
import { useCustomBooks } from "@/pages/knowledge/useCustomBooks";
import { useKnowledgeRouteBase } from "@/pages/knowledge/useKnowledgeRouteBase";

/** Read-only article landing with rendered markdown. Edit stays on the edit route. */
export function ArticleViewPage() {
  const { articleId } = useParams();
  const baseId = useKnowledgeRouteBase();
  const { orgContextStatus, orgContextError } = useAgents();
  const { books: customBooks } = useCustomBooks();
  const titles = new Map(customBooks.map((book) => [book.collectionKey, book.title] as const));

  const [article, setArticle] = useState<KnowledgeArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!articleId) {
      setError("Article id is missing.");
      setLoading(false);
      return;
    }
    if (orgContextStatus === "loading") {
      setLoading(true);
      setError(null);
      return;
    }
    if (orgContextStatus === "missing") {
      setArticle(null);
      setError(orgContextError || "Organization context required.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      const res = await apiGet<KnowledgeArticle & { error?: string }>(
        `/api/knowledge/articles/${encodeURIComponent(articleId)}`,
      );
      if (cancelled) return;
      if (!res.ok || !res.data?.id) {
        setError(res.error || "Could not load article.");
        setArticle(null);
        setLoading(false);
        return;
      }
      setArticle(res.data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [articleId, orgContextStatus, orgContextError]);

  const bookKeys = article ? article.collectionKeys.filter(isBookCollectionKey) : [];
  const bookLabels =
    bookKeys.length > 0
      ? bookKeys.map((key) => titles.get(key) || key).join(", ")
      : "None (standalone)";

  return (
    <KnowledgeChrome
      title={article?.title || "Article"}
      description="Read this article from the shared knowledge store. Use Edit to change the title, status, body, or optional books."
      badge={article ? knowledgeStatusLabel(article.status) : undefined}
      actions={
        <>
          <Link to={agentsHref("/knowledge/articles")} className={knowledgeSecondaryLinkClass}>
            Articles
          </Link>
          {articleId ? (
            <Link
              to={agentsHref(articleEditPath(baseId, articleId))}
              className={knowledgePrimaryLinkClass}
            >
              <Pencil className="h-4 w-4" aria-hidden />
              Edit
            </Link>
          ) : null}
        </>
      }
    >
      {error ? <KnowledgeError message={error} /> : null}
      {loading ? <p className="text-sm text-slate-500 dark:text-zinc-500">Loading article…</p> : null}
      {!loading && article ? (
        <div className="max-w-3xl space-y-6 rounded-lg border border-slate-200 bg-white dark:border-white/10 dark:bg-zinc-950/40 p-6">
          <dl className="grid gap-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-medium text-slate-600 dark:text-zinc-300">Status</dt>
              <dd className="mt-1 font-medium text-slate-900 dark:text-white">
                {knowledgeStatusLabel(article.status)}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-600 dark:text-zinc-300">Books</dt>
              <dd className="mt-1 font-medium text-slate-900 dark:text-white">{bookLabels}</dd>
            </div>
            <div>
              <dt className="font-medium text-slate-600 dark:text-zinc-300">Updated</dt>
              <dd className="mt-1 font-medium text-slate-900 dark:text-white">
                {formatKnowledgeDate(article.updatedAt)}
              </dd>
            </div>
          </dl>
          <div>
            <h2 className="text-sm font-medium text-slate-600 dark:text-zinc-300">Body</h2>
            <MarkdownArticleBody markdown={article.markdown || ""} />
          </div>
        </div>
      ) : null}
    </KnowledgeChrome>
  );
}
